import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { saveApiKey } from "@/services/heygen/saveApiKey";
import { getApiKey } from "@/services/heygen/getApiKey";
import ApiKeyStore from "@/stores/ApiKeyStore";

export function useApiKeyInput() {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const queryClient = useQueryClient();

  // Use the API key store
  const localApiKey = ApiKeyStore(state => state.localApiKey);
  const hasChanges = ApiKeyStore(state => state.hasChanges);
  const setLocalApiKey = ApiKeyStore(state => state.setLocalApiKey);
  const setOriginalApiKey = ApiKeyStore(state => state.setOriginalApiKey);

  const { data: apiKey = "", isLoading } = useQuery({
    queryKey: ["apiKey"],
    queryFn: getApiKey,
  });

  // Sync store with query data when it changes
  useEffect(() => {
    setOriginalApiKey(apiKey);
  }, [apiKey, setOriginalApiKey]);

  const { mutate: saveApiKeyMutation, isPending: isSaving } = useMutation({
    mutationFn: (key: string) => saveApiKey(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apiKey"] });
      // Reset changes after successful save
      setOriginalApiKey(localApiKey);
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalApiKey(e.target.value.trim());
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSaveApiKey = () => {
    // Ensure no leading/trailing spaces before saving
    const trimmedApiKey = localApiKey.trim();
    setLocalApiKey(trimmedApiKey);
    saveApiKeyMutation(trimmedApiKey);
  };

  return {
    apiKey: localApiKey,
    isLoading: isLoading || isSaving,
    showPassword,
    hasChanges,
    handleChange,
    togglePasswordVisibility,
    handleSaveApiKey,
  };
}
